/**
 * HistoryDrawer — выезжающая панель «История изменений» транша (GET /history?paymentId=).
 * Пополевой diff старое→новое; деньги форматируются formatRub, даты — fmtDate.
 */

import type { HistoryEntryDto } from '../../lib/types';
import { formatRub } from '../../lib/money';
import { fmtDate, fmtDateTime } from './lib/dates';
import { Drawer } from './ui/Drawer';
import { IconArrowRight } from './ui/icons';
import { useHistory, type DealScope } from './api';

const FIELD_LABELS: Record<string, string> = {
  plannedDate: 'Дата план',
  actualDate: 'Дата факт',
  plannedAmountMinor: 'Плановая сумма',
  paidAmountMinor: 'Фактическая сумма',
  plannedAmount: 'Плановая сумма',
  paidAmount: 'Фактическая сумма',
  status: 'Статус',
  comment: 'Комментарий',
};

const MONEY_FIELDS = new Set(['plannedAmountMinor', 'paidAmountMinor', 'plannedAmount', 'paidAmount']);
const DATE_FIELDS = new Set(['plannedDate', 'actualDate']);

function renderValue(field: string, value: string | null): string {
  if (value === null || value === '') return '—';
  if (MONEY_FIELDS.has(field)) {
    try {
      return formatRub(value);
    } catch {
      return value;
    }
  }
  if (DATE_FIELDS.has(field)) return fmtDate(value);
  return value;
}

function actorName(entry: HistoryEntryDto): string {
  if (entry.changedBy.name) return entry.changedBy.name;
  if (entry.changedBy.amoUserId) return `Пользователь ${entry.changedBy.amoUserId}`;
  return 'Система';
}

export interface HistoryDrawerProps {
  scope: DealScope;
  paymentId: string;
  paymentNumber: number;
  onClose: () => void;
}

export function HistoryDrawer({ scope, paymentId, paymentNumber, onClose }: HistoryDrawerProps): JSX.Element {
  const query = useHistory(scope, paymentId);
  const entries = query.data?.data ?? [];

  return (
    <Drawer title={`История транша №${paymentNumber}`} onClose={onClose}>
      {query.isLoading ? (
        <div className="pw-drawer__loading" aria-busy="true">
          <span className="pw-spinner" aria-hidden />
        </div>
      ) : query.isError ? (
        <p className="pw-field__err" role="alert">
          Не удалось загрузить историю изменений.
        </p>
      ) : entries.length === 0 ? (
        <p className="pw-muted">Изменений ещё не было.</p>
      ) : (
        <ul className="pw-hist">
          {entries.map((h) => (
            <li key={h.id} className="pw-hist__item">
              <div className="pw-hist__head">
                <span className="pw-hist__field">{FIELD_LABELS[h.field] ?? h.field}</span>
                <time className="pw-hist__time">{fmtDateTime(h.changedAt)}</time>
              </div>
              <div className="pw-hist__diff">
                <span className="pw-hist__old">{renderValue(h.field, h.oldValue)}</span>
                <span className="pw-hist__arrow" aria-hidden>
                  <IconArrowRight size={14} />
                </span>
                <span className="pw-hist__new">{renderValue(h.field, h.newValue)}</span>
              </div>
              <div className="pw-hist__who">{actorName(h)}</div>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
